import { ICareerSkillNode } from 'core/hooks/CareerSkillGraph/transform/transformCareerSkillGraph';

import { ISkillGraphIndex, isClosed } from './skillGraphModel';

/**
 * Отбор узлов карты навыков (ТЗ 0070, доводка).
 *
 * 🔴 Фильтр живёт ЗДЕСЬ, а не в раскладке. Списочный режим рисуется прямо из
 * индекса и раскладку не видит вовсе — предикат внутри `layoutSkillGraph`
 * пришлось бы написать второй раз для списка, а два экземпляра одного предиката
 * это и есть механизм, которым «на холсте три навыка, а в списке двенадцать».
 * Одна чистая функция выше по течению закрывает требование «фильтры работают в
 * обоих видах одинаково» по построению, а не по дисциплине.
 *
 * 🔴 Сужаются ТОЛЬКО `clusters` и `nodesOf`. `byId`, `bySlug`, рёбра и
 * `progressOf` проходят насквозь: иначе `?skill=` на отфильтрованный навык
 * перестанет открывать паспорт, связи начнут теряться молча, а кольцо доли
 * начнёт считать по остатку.
 */

/** Что показывать по отношению студента к навыку. */
export type SkillHaveFilter = 'gap' | 'mine';

/**
 * 🔴 Группы (`focus`) здесь больше НЕТ (ТЗ 0071, п. 6.1). Раньше фокус был
 * одновременно фильтром и выбрасывал соседние группы из индекса; теперь он
 * только раскрывает группу, а свёрнутые соседи остаются на карте. Считать его
 * фильтром значило бы зажигать «сбросить фильтры» на каждом клике по группе.
 */
export interface ISkillGraphFilters {
  have: SkillHaveFilter | null;
  core: boolean;
  course: string | null;
}

export const EMPTY_FILTERS: ISkillGraphFilters = {
  have: null,
  core: false,
  course: null,
};

export const hasActiveFilters = (filters: ISkillGraphFilters): boolean =>
  Boolean(filters.have || filters.core || filters.course);

/**
 * 🔴 «Не хватает» — это ТРИ статуса, и `optional` с `hidden` в них не входят.
 * Легенда на том же экране называет их «Дополнительная» и «Скрыта»; показать их
 * под подписью «Не хватает» значит противоречить собственной легенде. Отсюда
 * же следует, что «не хватает» и «уже есть» в сумме дают не всё — ровно поэтому
 * у переключателя есть третье состояние «Все».
 */
const isGap = (node: ICareerSkillNode): boolean =>
  node.status === 'missing_critical' || node.status === 'missing' || node.status === 'in_progress';

export const matchesFilters = (node: ICareerSkillNode, filters: ISkillGraphFilters): boolean => {
  if (filters.have === 'gap' && !isGap(node)) return false;
  if (filters.have === 'mine' && !isClosed(node)) return false;
  if (filters.core && node.priority !== 'core') return false;
  if (filters.course && !node.providers.some((row) => row.courseId === filters.course)) {
    return false;
  }
  return true;
};

export interface IFilteredGraph {
  index: ISkillGraphIndex;
  /** Сколько узлов осталось и сколько было — подпись «показано N из M». */
  shown: number;
  total: number;
}

/**
 * Курсы, которые дают хоть один навык этой карты. Собираются из самого графа —
 * ни одного лишнего запроса. Порядок стабильный и без `localeCompare`: тот
 * сортирует по таблицам ICU, а они разъезжаются между сборками.
 */
export const coursesOf = (index: ISkillGraphIndex): { id: string; name: string; n: number }[] => {
  const seen = new Map<string, { id: string; name: string; n: number }>();
  Array.from(index.byId.values()).forEach((node) => {
    node.providers.forEach((row) => {
      const found = seen.get(row.courseId);
      if (found) {
        found.n += 1;
        return;
      }
      seen.set(row.courseId, { id: row.courseId, name: row.courseName, n: 1 });
    });
  });
  return Array.from(seen.values()).sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
};

/**
 * Применить отбор к индексу.
 *
 * `selected` не выбрасывается НИКОГДА — то же правило, по которому обрезка
 * колонки втягивает выбранный узел обратно. Иначе «кликнул навык → включил
 * Ключевые» оставляет открытый паспорт над пустым местом.
 *
 * Битые значения ведут себя как «фильтра нет», а не как «ничего не подошло»:
 * ссылка с кодом группы или курса от другой профессии не имеет права отдать
 * пустой экран.
 */
export const filterSkillGraph = (
  index: ISkillGraphIndex,
  filters: ISkillGraphFilters,
  selected: string | null,
): IFilteredGraph => {
  const total = index.byId.size;
  const course =
    filters.course &&
    Array.from(index.byId.values()).some((node) =>
      node.providers.some((row) => row.courseId === filters.course),
    )
      ? filters.course
      : null;
  const active: ISkillGraphFilters = { ...filters, course };

  if (!hasActiveFilters(active)) return { index, shown: total, total };

  const nodesOf = new Map<string, ICareerSkillNode[]>();
  let shown = 0;
  index.clusters.forEach((cluster) => {
    const rows = (index.nodesOf.get(cluster.code) || []).filter(
      (node) => node.id === selected || matchesFilters(node, active),
    );
    if (!rows.length) return;
    nodesOf.set(cluster.code, rows);
    shown += rows.length;
  });

  const clusters = index.clusters.filter((cluster) => nodesOf.has(cluster.code));

  return { index: { ...index, clusters, nodesOf }, shown, total };
};
